import { createQuery } from "@tanstack/solid-query";
import { api, fetchApi } from "@/lib/api";
import {
  type components,
  createOrganizationMembersHandlers,
  generateId,
  type MemberManagementActions as OrganizationMembersActions,
  type OrganizationRole,
  queryKeys,
} from "@listto/shared";
import { useSession } from "@/lib/auth";

export function useOrganizationMembers(organizationId: () => string | undefined) {
  const session = useSession();

  const members = createQuery<
    components["schemas"]["OrganizationMemberDTO"][],
    components["schemas"]["ErrorModel"]
  >(() => ({
    queryKey: (() => {
      const orgId = organizationId();
      return queryKeys.auth.members(orgId ?? "none");
    })(),
    queryFn: async () => {
      const orgId = organizationId();
      if (!orgId) {
        throw new Error("No organizationId");
      }
      return fetchApi(
        api.GET("/api/org/listMembers", {
          params: { query: { organizationId: orgId } },
        }),
      );
    },
    enabled: () => Boolean(session.data) && Boolean(organizationId()),
  }));

  const actions: OrganizationMembersActions<OrganizationRole> = {
    inviteMember: async (email: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/inviteMember", {
          body: {
            id: generateId(),
            organizationId: orgId,
            email,
            role,
          },
        }),
      );
      await members.refetch();
    },
    removeMember: async (memberIdOrUserId: string) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/removeMember", {
          body: {
            id: memberIdOrUserId,
          },
        }),
      );
      await members.refetch();
    },
    updateMemberRole: async (memberId: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/updateMemberRole", {
          body: {
            id: memberId,
            role,
          },
        }),
      );
      await members.refetch();
    },
  };

  const {
    handleInviteMember: inviteMember,
    handleRemoveMember: removeMember,
    handleUpdateMemberRole: updateMemberRole,
  } = createOrganizationMembersHandlers(actions);

  return {
    members: () => members.data ?? [],
    isLoading: () => members.isLoading,
    inviteMember,
    removeMember,
    updateMemberRole,
  };
}
